import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
} from '@mui/material';
import { SimpleSelect } from 'components/CustomSelectFields';
import { getBatchUsage } from '../../services/analyticsV2';
import { DurationOptions, InterviewVariantTypes, isCaseVariant } from './constant';

const CARD_STYLES = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '8px',
  p: 4,
  boxShadow: '0px 0px 3px 0px #00000080',
};

/** One usage figure: a label, a count, and an optional illustration. */
const UsageCard = ({ label, labelColor, value, image, muted = false }) => (
  <Paper elevation={0} sx={CARD_STYLES}>
    <Box>
      <Typography variant='h6-semiBold' color={labelColor}>
        {label}
      </Typography>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, ...(muted ? { color: 'neutral.clr-600' } : {}) }}
      >
        {value}
      </Typography>
    </Box>
    {image ? (
      <img
        src={image}
        alt=""
        width={60}
        height={60}
        style={muted ? { opacity: 0.5 } : undefined}
      />
    ) : null}
  </Paper>
);

const EMPTY_USAGE = {
  finishedAttempts: 0,
  unfinishedAttempts: 0,
  droppedMidSession: 0,
  proctoredIssues: 0,
};

const BASE_CARDS = [
  {
    key: 'finishedAttempts',
    label: 'Finished attempts',
    labelColor: 'success.clr-500',
    image: 'https://assets.languify.in/images/usageF.png',
  },
  {
    key: 'unfinishedAttempts',
    label: 'Unfinished attempts',
    labelColor: 'danger.clr-500',
    image: 'https://assets.languify.in/images/usageC.png',
  },
];

// Case attempts are a proctored, single-sitting format, so they report two
// figures the other variants have no equivalent for. No illustration for these
// two, matching the design.
const CASE_CARDS = [
  {
    key: 'droppedMidSession',
    label: 'Who Dropped Mid-session',
    labelColor: 'danger.clr-500',
  },
  {
    key: 'proctoredIssues',
    label: 'Proctored Issues Found',
    labelColor: 'danger.clr-500',
  },
];

const Usage = ({ batchId, interviewVariantType = InterviewVariantTypes.VERBAL_MOCK }) => {
  const [duration, setDuration] = useState(DurationOptions.find(opt => opt._id === DurationOptions[4]._id) || 'last_3_months');
  const [usageData, setUsageData] = useState(EMPTY_USAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isCase = isCaseVariant(interviewVariantType);

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setLoading(true);
        const response = await getBatchUsage(batchId, interviewVariantType, duration._id);
        setUsageData({ ...EMPTY_USAGE, ...(response || {}) });
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching usage data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (batchId) {
      fetchUsageData();
    }
  }, [batchId, interviewVariantType, duration]);

  const handleDurationChange = (e) => {
    const duration = e.target.value;
    setDuration(DurationOptions.find(opt => opt._id === duration));
  };

  const cards = useMemo(
    () => (isCase ? [...BASE_CARDS, ...CASE_CARDS] : BASE_CARDS),
    [isCase]
  );

  // Loading, error and loaded all render the same card list, so the panel keeps
  // its shape instead of growing from two cards to four once data lands.
  const renderValue = (key) => {
    if (loading) return <Skeleton variant="text" width={80} height={40} />;
    if (error) return 'N/A';

    return usageData[key] ?? 0;
  };

  return (
    <Box display="flex" flexDirection="column" gap="12px" width="50%">
      <Typography variant="h4-medium">Usage</Typography>

      <SimpleSelect
        options={DurationOptions}
        value={duration._id}
        onSelect={handleDurationChange}
        sx={{ width: '100%' }}
        label='Duration'
      />

      <Box display='flex' flexDirection='column' gap={4} mt={1}>
        {cards.map(({ key, label, labelColor, image }) => (
          <UsageCard
            key={key}
            label={label}
            labelColor={labelColor}
            image={image}
            value={renderValue(key)}
            muted={Boolean(error)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Usage;
